import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import {
	IAdminUser,
	copyAdminDatasetPermissions,
	useAdminUsersQuery,
	useAdminUserDatasetPermissionsQuery,
} from '../../api/admin';

interface Props {
	open: boolean;
	onClose: () => void;
	userId: number;
	userEmail: string;
}

export default function CopyDatasetPermissionsDialog({ open, onClose, userId, userEmail }: Props) {
	const queryClient = useQueryClient();
	const { data: allUsers, isLoading: usersLoading } = useAdminUsersQuery();

	const [step, setStep] = useState<'select' | 'confirm'>('select');
	const [sourceUser, setSourceUser] = useState<IAdminUser | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [copiedCount, setCopiedCount] = useState<number | null>(null);

	const { data: sourcePermissions } = useAdminUserDatasetPermissionsQuery(
		sourceUser?.id ?? 0
	);

	const availableUsers = (allUsers ?? []).filter((u) => u.id !== userId);

	function resetAndClose() {
		setStep('select');
		setSourceUser(null);
		setError(null);
		setCopiedCount(null);
		onClose();
	}

	async function handleConfirm() {
		if (!sourceUser) return;
		setSubmitting(true);
		setError(null);
		try {
			const accessFrom = new Date().toISOString().split('T')[0];
			const results = await copyAdminDatasetPermissions(userId, sourceUser.id, accessFrom);
			setCopiedCount(results.length);
			await queryClient.invalidateQueries(['admin-user-dataset-permissions', userId]);
			resetAndClose();
		} catch (e: any) {
			setError(e?.response?.data?.detail ?? 'An unexpected error occurred. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}

	const sourceUserLabel = sourceUser
		? sourceUser.full_name
			? `${sourceUser.full_name} (${sourceUser.email})`
			: sourceUser.email
		: '';

	return (
		<Dialog
			open={open}
			onClose={resetAndClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{step === 'select' ? (
				<>
					<DialogTitle>Copy Permissions From User</DialogTitle>
					<DialogContent sx={{ pt: 2 }}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Select a user to copy all of their dataset permissions to{' '}
							<strong>{userEmail}</strong>, including any ticker subset on a
							permission. Datasets the target already has access to will be skipped.
						</Typography>
						<Autocomplete
							options={availableUsers}
							loading={usersLoading}
							value={sourceUser}
							onChange={(_, value) => setSourceUser(value)}
							getOptionLabel={(u) =>
								u.full_name ? `${u.full_name} (${u.email})` : u.email
							}
							isOptionEqualToValue={(a, b) => a.id === b.id}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Source user"
									placeholder="Search by name or email…"
									size="small"
								/>
							)}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose}>Cancel</Button>
						<Button
							variant="contained"
							onClick={() => setStep('confirm')}
							disabled={!sourceUser}
						>
							Next
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>Confirm Copy Permissions</DialogTitle>
					<DialogContent>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<Typography gutterBottom>
							Copy all dataset permissions from{' '}
							<strong>{sourceUserLabel}</strong> to{' '}
							<strong>{userEmail}</strong>?
						</Typography>
						{sourcePermissions && sourcePermissions.length > 0 ? (
							<>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
									{sourcePermissions.length} dataset{sourcePermissions.length !== 1 ? 's' : ''} will be attempted:
								</Typography>
								<ul style={{ margin: 0, paddingLeft: 20 }}>
									{sourcePermissions.map((p) => (
										<li key={p.id}>
											<Typography variant="body2">
												<strong>{p.vendor_name}</strong> — {p.dataset_name}
												{p.ticker_subset ? ` (tickers: ${p.ticker_subset})` : ''}
											</Typography>
										</li>
									))}
								</ul>
							</>
						) : (
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								{sourcePermissions ? 'This user has no active dataset permissions.' : 'Loading…'}
							</Typography>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setStep('select')} disabled={submitting}>
							Back
						</Button>
						<Button
							variant="contained"
							color="success"
							onClick={handleConfirm}
							disabled={submitting || (sourcePermissions?.length ?? 0) === 0}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting ? 'Copying…' : 'Confirm Copy'}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
